import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { legacyOffersAPI, projectsAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import IncognitoToggleButton from "../components/IncognitoToggleButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Save,
  Loader2,
  Euro,
  Calendar,
  Building2,
  Receipt,
  History,
  ExternalLink,
  Plus,
  Percent,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileCheck,
  Trash2
} from "lucide-react";

const statusConfig = {
  importiert: { label: "Importiert", color: "bg-gray-100 text-gray-800" },
  geprüft: { label: "Geprüft", color: "bg-blue-100 text-blue-800" },
  freigegeben: { label: "Freigegeben", color: "bg-green-100 text-green-800" },
  beauftragt: { label: "Beauftragt", color: "bg-purple-100 text-purple-800" },
  teilfakturiert: { label: "Teilfakturiert", color: "bg-amber-100 text-amber-800" },
  "vollständig_fakturiert": { label: "Vollständig fakturiert", color: "bg-emerald-100 text-emerald-800" }
};

const documentTypeLabels = {
  angebot: "Angebot",
  "auftragsbestätigung": "Auftragsbestätigung"
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR"
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("de-DE");
  } catch {
    return dateStr;
  }
};

export default function LegacyOfferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [offer, setOffer] = useState(null);
  const [history, setHistory] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("stammdaten");
  
  // Edit state
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Invoice dialog
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceType, setInvoiceType] = useState("percentage");
  const [invoicePercentage, setInvoicePercentage] = useState("60");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // PDF viewer
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [offerRes, historyRes] = await Promise.all([
        legacyOffersAPI.get(id),
        legacyOffersAPI.getHistory(id)
      ]);
      
      setOffer(offerRes.data);
      setFormData(offerRes.data);
      setHistory(historyRes.data);
      
      // Load projects for this customer only
      const customerId = offerRes.data.customer_id;
      if (customerId) {
        const projectsRes = await projectsAPI.list(null, false, customerId);
        setProjects(projectsRes.data || []);
      } else {
        setProjects([]);
      }
      
    } catch (error) {
      console.error("Failed to load legacy offer:", error);
      toast.error("Legacy-Angebot konnte nicht geladen werden");
      navigate("/kunden");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-open invoice dialog when arriving with ?openInvoice (any non-empty value)
  useEffect(() => {
    if (!offer) return;
    if (searchParams.get("openInvoice")) {
      const eligible = ["freigegeben", "beauftragt", "teilfakturiert"];
      if (eligible.includes(offer.status)) {
        setShowInvoiceDialog(true);
      } else {
        toast.error(`Rechnung nicht möglich – Status "${offer.status}". Erst auf "freigegeben" setzen.`);
      }
      // Remove the query param so refresh/back doesn't re-trigger
      searchParams.delete("openInvoice");
      setSearchParams(searchParams, { replace: true });
    }
  }, [offer, searchParams, setSearchParams]);

  const loadPdf = async () => {
    if (!offer?.legacy_source_file) return;
    
    try {
      setLoadingPdf(true);
      const response = await legacyOffersAPI.getPdfUrl(id);
      setPdfUrl(response.data.url);
    } catch (error) {
      console.error("Failed to load PDF:", error);
      toast.error("PDF konnte nicht geladen werden");
    } finally {
      setLoadingPdf(false);
    }
  };

  useEffect(() => {
    if (activeTab === "originaldokument" && !pdfUrl && offer?.legacy_source_file) {
      loadPdf();
    }
  }, [activeTab, offer]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    try {
      setSaving(true);
      await legacyOffersAPI.update(id, formData);
      toast.success("Änderungen gespeichert");
      setHasChanges(false);
      loadData();
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvoice = async () => {
    try {
      setCreatingInvoice(true);
      
      const data = {
        notes: invoiceNotes || null
      };
      
      if (invoiceType === "percentage") {
        data.percentage = parseFloat(invoicePercentage);
      } else {
        data.fixed_amount = parseFloat(invoiceAmount);
      }
      
      await legacyOffersAPI.createInvoice(id, data);
      toast.success("Abschlagsrechnung erstellt");
      setShowInvoiceDialog(false);
      setInvoiceNotes("");
      loadData();
    } catch (error) {
      console.error("Failed to create invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Rechnung");
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await legacyOffersAPI.delete(id);
      toast.success("Legacy-Angebot gelöscht");
      navigate(-1);
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const canCreateInvoice = () => {
    if (!offer) return false;
    const validStatuses = ["freigegeben", "beauftragt", "teilfakturiert"];
    return validStatuses.includes(offer.status);
  };

  const canDelete = () => {
    if (!offer) return false;
    return !offer.invoices || offer.invoices.length === 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Legacy-Angebot nicht gefunden</p>
      </div>
    );
  }

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || { label: status, color: "bg-gray-100" };
    return (
      <Badge className={config.color} variant="outline">
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{offer.title || "Legacy-Angebot"}</h1>
              <StatusBadge status={offer.status} />
              <Badge variant="secondary">
                {documentTypeLabels[offer.document_type] || offer.document_type}
              </Badge>
              <IncognitoBadge active={offer.is_incognito} size="md" />
            </div>
            <p className="text-muted-foreground">
              {offer.offer_reference && `Ref: ${offer.offer_reference} • `}
              {offer.customer_name}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <IncognitoToggleButton user={user} docType="legacy_offer" docId={offer.id} active={offer.is_incognito} onToggled={loadData} />
          {hasChanges && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
          )}
          
          {canDelete() && isAdmin && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Löschen
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Euro className="h-4 w-4" />
              <span className="text-sm">Gesamtbetrag</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(offer.gross_amount)}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Receipt className="h-4 w-4" />
              <span className="text-sm">Fakturiert</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">
              {formatCurrency(offer.total_invoiced || 0)}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Offen</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(offer.remaining_amount || offer.gross_amount)}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Percent className="h-4 w-4" />
              <span className="text-sm">Fakturierungsgrad</span>
            </div>
            <p className="text-2xl font-bold">
              {offer.gross_amount > 0 
                ? Math.round(((offer.total_invoiced || 0) / offer.gross_amount) * 100)
                : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="stammdaten" data-testid="tab-stammdaten">Stammdaten</TabsTrigger>
          <TabsTrigger value="inhalt" data-testid="tab-inhalt">Inhalt</TabsTrigger>
          <TabsTrigger value="summen" data-testid="tab-summen">Summen</TabsTrigger>
          <TabsTrigger value="originaldokument" data-testid="tab-original">Original</TabsTrigger>
          <TabsTrigger value="rechnungen" data-testid="tab-rechnungen">Rechnungen</TabsTrigger>
          <TabsTrigger value="historie" data-testid="tab-historie">Historie</TabsTrigger>
        </TabsList>

        {/* Stammdaten Tab */}
        <TabsContent value="stammdaten" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Stammdaten</CardTitle>
              <CardDescription>Grundlegende Informationen zum Legacy-Angebot</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titel</Label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    data-testid="input-title"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Dokumenttyp</Label>
                  <Select
                    value={formData.document_type || "angebot"}
                    onValueChange={(v) => handleInputChange("document_type", v)}
                  >
                    <SelectTrigger data-testid="select-document-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="angebot">Angebot</SelectItem>
                      <SelectItem value="auftragsbestätigung">Auftragsbestätigung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status || "importiert"}
                    onValueChange={(v) => handleInputChange("status", v)}
                    disabled={offer.status === "vollständig_fakturiert"}
                  >
                    <SelectTrigger data-testid="select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="importiert">Importiert</SelectItem>
                      <SelectItem value="geprüft">Geprüft</SelectItem>
                      <SelectItem value="freigegeben">Freigegeben</SelectItem>
                      <SelectItem value="beauftragt">Beauftragt</SelectItem>
                      <SelectItem value="teilfakturiert">Teilfakturiert</SelectItem>
                      <SelectItem value="vollständig_fakturiert">Vollständig fakturiert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Dokumentdatum</Label>
                  <Input
                    type="date"
                    value={formData.document_date?.split("T")[0] || ""}
                    onChange={(e) => handleInputChange("document_date", e.target.value)}
                    data-testid="input-document-date"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Angebotsreferenz / -nummer</Label>
                  <Input
                    value={formData.offer_reference || ""}
                    onChange={(e) => handleInputChange("offer_reference", e.target.value)}
                    placeholder="z.B. ANG-2024-001"
                    data-testid="input-offer-reference"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Projektnummer (extern)</Label>
                  <Input
                    value={formData.project_number || ""}
                    onChange={(e) => handleInputChange("project_number", e.target.value)}
                    placeholder="z.B. P-2024-001"
                    data-testid="input-project-number"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Mit CRM-Projekt verknüpfen</Label>
                  <Select
                    value={formData.project_id || "none"}
                    onValueChange={(v) => handleInputChange("project_id", v === "none" ? null : v)}
                  >
                    <SelectTrigger data-testid="select-project">
                      <SelectValue placeholder="Kein Projekt verknüpft" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Projekt verknüpft</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.project_number} - {p.customer_name || "Unbekannt"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label>Interne Notizen</Label>
                <Textarea
                  value={formData.notes_internal || ""}
                  onChange={(e) => handleInputChange("notes_internal", e.target.value)}
                  placeholder="Interne Notizen (nicht auf Dokumenten sichtbar)"
                  rows={3}
                  data-testid="textarea-notes"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inhalt Tab */}
        <TabsContent value="inhalt" className="space-y-4 mt-6">
          {/* Positionen Section */}
          {formData.positions && formData.positions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Positionen ({formData.positions.length})
                </CardTitle>
                <CardDescription>Aus dem Dokument extrahierte Einzelpositionen</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium w-12">Pos.</th>
                        <th className="px-3 py-2 text-left font-medium">Beschreibung</th>
                        <th className="px-3 py-2 text-right font-medium w-20">Menge</th>
                        <th className="px-3 py-2 text-left font-medium w-16">Einheit</th>
                        <th className="px-3 py-2 text-right font-medium w-28">Einzelpreis</th>
                        <th className="px-3 py-2 text-right font-medium w-28">Gesamt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {formData.positions.map((pos, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="px-3 py-2 text-muted-foreground">
                            {pos.position_number || idx + 1}
                          </td>
                          <td className="px-3 py-2">
                            <div>
                              {pos.article_number && (
                                <span className="text-xs text-muted-foreground mr-2">
                                  [{pos.article_number}]
                                </span>
                              )}
                              {pos.description}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">{pos.quantity}</td>
                          <td className="px-3 py-2">{pos.unit}</td>
                          <td className="px-3 py-2 text-right">
                            {pos.unit_price ? formatCurrency(pos.unit_price) : "-"}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {pos.total_price ? formatCurrency(pos.total_price) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Freitext Section */}
          <Card>
            <CardHeader>
              <CardTitle>Freitext-Inhalte</CardTitle>
              <CardDescription>
                {formData.positions && formData.positions.length > 0 
                  ? "Zusätzliche Texte neben den strukturierten Positionen"
                  : "Leistungsbeschreibung und Texte"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Kopftext</Label>
                <Textarea
                  value={formData.free_text_header || ""}
                  onChange={(e) => handleInputChange("free_text_header", e.target.value)}
                  placeholder="Einleitungstext..."
                  rows={3}
                  data-testid="textarea-header"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Haupttext / Leistungsbeschreibung</Label>
                <Textarea
                  value={formData.free_text_body || ""}
                  onChange={(e) => handleInputChange("free_text_body", e.target.value)}
                  placeholder="Leistungsbeschreibung..."
                  rows={6}
                  data-testid="textarea-body"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Fußtext</Label>
                <Textarea
                  value={formData.free_text_footer || ""}
                  onChange={(e) => handleInputChange("free_text_footer", e.target.value)}
                  placeholder="Abschlusstext, Zahlungsbedingungen..."
                  rows={3}
                  data-testid="textarea-footer"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summen Tab */}
        <TabsContent value="summen" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Beträge</CardTitle>
              <CardDescription>Netto, MwSt. und Brutto</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nettobetrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.net_amount || ""}
                    onChange={(e) => handleInputChange("net_amount", parseFloat(e.target.value) || 0)}
                    data-testid="input-net-amount"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>MwSt.-Satz (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.vat_rate ?? 19}
                    onChange={(e) => handleInputChange("vat_rate", parseFloat(e.target.value) || 19)}
                    data-testid="input-vat-rate"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>MwSt.-Betrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.vat_amount || ""}
                    onChange={(e) => handleInputChange("vat_amount", parseFloat(e.target.value) || 0)}
                    data-testid="input-vat-amount"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Bruttobetrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.gross_amount || ""}
                    onChange={(e) => handleInputChange("gross_amount", parseFloat(e.target.value) || 0)}
                    className="font-bold"
                    data-testid="input-gross-amount"
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rabatttyp</Label>
                  <Select
                    value={formData.discount_type || "keine"}
                    onValueChange={(v) => handleInputChange("discount_type", v)}
                  >
                    <SelectTrigger data-testid="select-discount-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keine">Kein Rabatt</SelectItem>
                      <SelectItem value="prozent">Prozent</SelectItem>
                      <SelectItem value="absolut">Absolut (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Rabattwert</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.discount_value || ""}
                    onChange={(e) => handleInputChange("discount_value", parseFloat(e.target.value) || 0)}
                    disabled={formData.discount_type === "keine"}
                    data-testid="input-discount-value"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Originaldokument Tab */}
        <TabsContent value="originaldokument" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Original-PDF
              </CardTitle>
              <CardDescription>
                Das unverändert archivierte Originaldokument
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!offer.legacy_source_file ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Kein Original-PDF vorhanden</p>
                  <p className="text-sm">Dieses Angebot wurde manuell erstellt</p>
                </div>
              ) : loadingPdf ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : pdfUrl ? (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button variant="outline" asChild>
                      <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        In neuem Tab öffnen
                      </a>
                    </Button>
                  </div>
                  <iframe
                    src={pdfUrl}
                    className="w-full h-[600px] border rounded-lg"
                    title="Original-PDF"
                  />
                </div>
              ) : (
                <Button onClick={loadPdf}>PDF laden</Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rechnungen Tab */}
        <TabsContent value="rechnungen" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Abschlagsrechnungen
                  </CardTitle>
                  <CardDescription>
                    Fakturierungsübersicht und neue Rechnungen erstellen
                  </CardDescription>
                </div>
                
                {canCreateInvoice() && isAdmin && (
                  <Button onClick={() => setShowInvoiceDialog(true)} data-testid="btn-create-invoice">
                    <Plus className="h-4 w-4 mr-2" />
                    Abschlagsrechnung erstellen
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Progress bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span>Fakturierungsfortschritt</span>
                  <span>
                    {formatCurrency(offer.total_invoiced || 0)} von {formatCurrency(offer.gross_amount)}
                  </span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{
                      width: `${Math.min(100, ((offer.total_invoiced || 0) / (offer.gross_amount || 1)) * 100)}%`
                    }}
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Offen: {formatCurrency(offer.remaining_amount || offer.gross_amount)}
                </p>
              </div>
              
              {!canCreateInvoice() && offer.status !== "vollständig_fakturiert" && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>
                    Um Rechnungen zu erstellen, muss das Angebot mindestens den Status "Freigegeben" haben.
                    Aktueller Status: {statusConfig[offer.status]?.label || offer.status}
                  </p>
                </div>
              )}
              
              {/* Invoice list */}
              {(!offer.invoices || offer.invoices.length === 0) ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Noch keine Rechnungen erstellt</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {offer.invoices.map((invoice, idx) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          invoice.status === "final" ? "bg-blue-100" : 
                          invoice.status === "storno" ? "bg-red-100" : "bg-amber-100"
                        }`}>
                          <Receipt className={`h-5 w-5 ${
                            invoice.status === "final" ? "text-blue-600" :
                            invoice.status === "storno" ? "text-red-600" : "text-amber-600"
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {invoice.invoice_number || "Entwurf"}
                            </span>
                            <Badge variant={invoice.status === "final" ? "default" : "secondary"}>
                              {invoice.status === "final" ? "Finalisiert" : 
                               invoice.status === "storno" ? "Storniert" : "Entwurf"}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Abschlagsrechnung {invoice.partial_number} ({invoice.partial_percentage}%)
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(invoice.gross_total)}</p>
                        <Link
                          to={`/rechnungen`}
                          className="text-sm text-primary hover:underline"
                        >
                          Details →
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Historie Tab */}
        <TabsContent value="historie" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Änderungshistorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Keine Historie vorhanden</p>
              ) : (
                <div className="space-y-4">
                  {history.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3 p-3 border rounded-lg">
                      <div className={`p-2 rounded-full ${
                        entry.event_type === "created" ? "bg-green-100" :
                        entry.event_type === "imported" ? "bg-blue-100" :
                        entry.event_type === "invoice_created" ? "bg-purple-100" :
                        "bg-gray-100"
                      }`}>
                        {entry.event_type === "created" && <Plus className="h-4 w-4 text-green-600" />}
                        {entry.event_type === "imported" && <FileText className="h-4 w-4 text-blue-600" />}
                        {entry.event_type === "updated" && <Save className="h-4 w-4 text-gray-600" />}
                        {entry.event_type === "invoice_created" && <Receipt className="h-4 w-4 text-purple-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">
                            {entry.event_type === "created" && "Manuell erstellt"}
                            {entry.event_type === "imported" && "PDF importiert"}
                            {entry.event_type === "updated" && "Bearbeitet"}
                            {entry.event_type === "invoice_created" && "Rechnung erstellt"}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          von {entry.user_name || "Unbekannt"}
                        </p>
                        {entry.event_data && entry.event_type === "invoice_created" && (
                          <p className="text-sm mt-1">
                            Betrag: {formatCurrency(entry.event_data.amount)} ({entry.event_data.percentage}%)
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invoice Creation Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Abschlagsrechnung erstellen</DialogTitle>
            <DialogDescription>
              Erstellen Sie eine neue Abschlagsrechnung für dieses Legacy-Angebot.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Gesamtbetrag:</span>
                <span className="font-medium">{formatCurrency(offer.gross_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Bereits fakturiert:</span>
                <span>{formatCurrency(offer.total_invoiced || 0)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium text-green-600">
                <span>Noch offen:</span>
                <span>{formatCurrency(offer.remaining_amount || offer.gross_amount)}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Berechnungsart</Label>
              <Select value={invoiceType} onValueChange={setInvoiceType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Prozentsatz</SelectItem>
                  <SelectItem value="fixed">Fester Betrag</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {invoiceType === "percentage" ? (
              <div className="space-y-2">
                <Label>Prozentsatz</Label>
                <Select value={invoicePercentage} onValueChange={setInvoicePercentage}>
                  <SelectTrigger data-testid="select-invoice-percentage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60">60%</SelectItem>
                    <SelectItem value="30">30%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="custom">Freier Prozentsatz</SelectItem>
                  </SelectContent>
                </Select>
                {invoicePercentage === "custom" && (
                  <Input
                    type="number"
                    placeholder="Prozentsatz eingeben"
                    onChange={(e) => setInvoicePercentage(e.target.value)}
                    className="mt-2"
                  />
                )}
                <p className="text-sm text-muted-foreground">
                  = {formatCurrency((offer.gross_amount || 0) * (parseFloat(invoicePercentage) || 0) / 100)}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Betrag (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  placeholder="0,00"
                  data-testid="input-invoice-amount"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notizen (optional)</Label>
              <Textarea
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                placeholder="Zusätzliche Notizen für die Rechnung..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateInvoice} disabled={creatingInvoice}>
              {creatingInvoice ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Receipt className="h-4 w-4 mr-2" />
              )}
              Rechnung erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Legacy-Angebot löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Das Angebot und alle zugehörigen Daten werden unwiderruflich gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
